import { useState } from "react";
import { Phone, Mail, MapPin, Upload, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SEO } from "../components/SEO";
import { useToast } from "../hooks/use-toast";
import { quoteFormData } from "../mockData";
import { trackEvent, GA_EVENTS } from "../components/GoogleAnalytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    urgency: "",
    message: ""
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('urgency', formData.urgency);
      formDataToSend.append('message', formData.message);
      
      // Append files
      files.forEach((file) => {
        formDataToSend.append('files', file);
      });

      // Submit to backend
      const response = await fetch(`${API_URL}/api/quotes/submit`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (response.ok) {
        // Track successful quote submission
        trackEvent(GA_EVENTS.QUOTE_SUBMITTED, {
          service: formData.service,
          urgency: formData.urgency,
          has_files: files.length > 0
        });

        toast({
          title: "✅ Quote Request Submitted!",
          description: result.message || "We'll get back to you within 24 hours.",
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          urgency: "",
          message: ""
        });
        setFiles([]);
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        
      } else {
        throw new Error(result.detail || 'Submission failed');
      }
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }
      toast({
        title: "❌ Submission Failed",
        description: error.message || "Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-navy-900">
      <SEO 
        title="Contact | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Magic C Plumbing – reliable plumbing and heating in London SW18 & SW19. Leak repairs, bathrooms, boilers and landlord services. Call 07398 873176."
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get Your Free Quote
            </h1>
            <p className="text-xl text-gray-300">
              Fill out the form below and our team will get back to you with a detailed estimate. 
              For emergencies, call us directly at <a href="tel:+447398873176" className="text-sage-400 hover:text-sage-300">07398 873176</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Contact Cards */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10 shadow-lg hover:shadow-xl hover:bg-white/10 transition-all">
              <CardContent className="p-6 text-center">
                <div className="bg-sage-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-sage-500/30">
                  <Phone className="h-8 w-8 text-sage-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Call Us 24/7</h3>
                <p className="text-gray-300 mb-3">Emergency services available</p>
                <a href="tel:07398873176" className="text-sage-400 hover:text-sage-300 font-medium text-lg block">
                  07398 873176
                </a>
                <a href="tel:03337890987" className="text-gray-400 hover:text-sage-400 font-medium text-sm block mt-1">
                  Office: 0333 789 0987
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/5 backdrop-blur-md border-white/10 shadow-lg hover:shadow-xl hover:bg-white/10 transition-all">
              <CardContent className="p-6 text-center">
                <div className="bg-sage-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-sage-500/30">
                  <Mail className="h-8 w-8 text-sage-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Email Us</h3>
                <p className="text-gray-300 mb-3">We reply within 24 hours</p>
                <a href="mailto:info@magiccplumbing.com" className="text-sage-400 hover:text-sage-300 font-medium">
                  info@magiccplumbing.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/5 backdrop-blur-md border-white/10 shadow-lg hover:shadow-xl hover:bg-white/10 transition-all">
              <CardContent className="p-6 text-center">
                <div className="bg-sage-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-sage-500/30">
                  <MapPin className="h-8 w-8 text-sage-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Visit Us</h3>
                <p className="text-gray-300 mb-3">Stop by our office</p>
                <p className="text-sage-400 font-medium">
                  London & Surrounding Areas
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quote Form */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/5 backdrop-blur-md border-white/10 shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    Request a Free Quote
                  </h2>
                  <p className="text-gray-300">
                    Tell us about your project and we'll provide a detailed estimate
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="07398 873176"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service">Service Needed *</Label>
                      <Select 
                        value={formData.service} 
                        onValueChange={(value) => handleSelectChange("service", value)}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {quoteFormData.services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="urgency">Project Timeline *</Label>
                    <Select 
                      value={formData.urgency} 
                      onValueChange={(value) => handleSelectChange("urgency", value)}
                      required
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="When do you need this done?" />
                      </SelectTrigger>
                      <SelectContent>
                        {quoteFormData.urgency.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Project Description *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="mt-1 min-h-32"
                      placeholder="Please describe your project in detail..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="files">Upload Photos/Videos (Optional)</Label>
                    <div className="mt-1">
                      <Input
                        id="files"
                        name="files"
                        type="file"
                        onChange={handleFileChange}
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf,.mp4,.mov,.avi"
                        className="cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-sage-600 file:text-white hover:file:bg-sage-700"
                      />
                      <p className="text-sm text-gray-400 mt-2">
                        <Upload className="inline h-4 w-4 mr-1" />
                        Accepted: JPG, PNG, PDF, MP4, MOV, AVI (Max 50MB per file)
                      </p>
                      {files.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-sage-400">
                            {files.length} file(s) selected: {files.map(f => f.name).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    disabled={isSubmitting}
                    className="w-full bg-sage-600 hover:bg-sage-700 text-white text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Calculate Your Savings"}
                  </Button>

                  <p className="text-sm text-gray-300 text-center">
                    By submitting this form, you agree to be contacted by MAGIC C PLUMBING regarding your request.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-16 bg-navy-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Clock className="h-8 w-8 text-sage-400" />
              <h2 className="text-3xl font-bold">Business Hours</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 text-center">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-sage-300">Regular Service</h3>
                <div className="space-y-2 text-gray-300">
                  <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                  <p>Saturday: 9:00 AM - 5:00 PM</p>
                  <p>Sunday: 10:00 AM - 4:00 PM</p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3 text-sage-300">Emergency Service</h3>
                <div className="space-y-2 text-gray-300">
                  <p className="text-2xl font-bold text-white">24/7</p>
                  <p>Available Every Day</p>
                  <p>Including Holidays</p>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="mt-10 text-center">
              <a href="tel:07398873176">
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-500 text-white text-lg px-10 py-6 font-semibold shadow-xl hover:shadow-sage-600/50 transition-all duration-300"
                >
                  Start Your Day Right - Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
